import { effect, Injectable, inject, untracked } from '@angular/core';
import { DateFnsAdapter } from '@angular/material-date-fns-adapter';
import { SettingsStore } from 'src/app/core/store/settings.store';
import { parseShorthandTime } from './shorthand-time';

/**
 * The date adapter every Material datepicker in the app resolves.
 *
 * `DateFnsAdapter` takes the first day of the week from the locale, which pins
 * every picker to Sunday under `enUS` no matter what the user chose — the
 * recordings calendar would open on Monday and the picker beside it on Sunday.
 * Reading the preference here is what keeps the two agreeing.
 *
 * It is also where shorthand time entry lives, so every timepicker in the app
 * accepts the same abbreviations.
 */
@Injectable()
export class SettingsDateAdapter extends DateFnsAdapter {
  private readonly settings = inject(SettingsStore);

  constructor() {
    super();

    /**
     * A calendar reads `getFirstDayOfWeek()` once, when it lays its grid out,
     * and re-runs that only on `localeChanges`. Re-setting the locale is how a
     * preference changed while a picker is open reaches the open picker; the
     * value itself is unchanged, so nothing else moves.
     */
    effect(() => {
      this.settings.calendarFirstDay();
      untracked(() => this.setLocale(this.locale));
    });
  }

  override getFirstDayOfWeek(): number {
    return this.settings.calendarFirstDay();
  }

  /**
   * Accepts a time typed without its punctuation — `1830`, `18.30`, `630p` —
   * on top of the format the field displays.
   *
   * The strict parse goes first and keeps its say: the canonical format is
   * what the field writes back on blur, and it must never lose to a looser
   * reading of the same text. Only what it rejects is offered to the shorthand,
   * so this can add entries but never reinterpret one.
   */
  override parseTime(
    value: unknown,
    parseFormat: string | string[],
  ): Date | null {
    const strict = super.parseTime(value, parseFormat);
    if (
      (strict !== null && this.isValid(strict)) ||
      typeof value !== 'string'
    ) {
      return strict;
    }

    const shorthand = parseShorthandTime(value);
    return shorthand === null
      ? strict
      : this.setTime(this.today(), shorthand.hours, shorthand.minutes, 0);
  }
}
